import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { IconButton } from './Button'
import { suppressTooltipsBriefly } from './Tooltip'
import { boundsFor } from '../../layout/segments'

interface MenuContextValue {
  /** Chiude il menu e riporta il focus sull'elemento che l'ha aperto. */
  close: () => void
}
const MenuContext = createContext<MenuContextValue | null>(null)

export interface MenuProps {
  /** Elemento che apre il menu: il menu si allinea a lui e gli restituisce il focus alla chiusura. */
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  'aria-label': string
  className?: string | undefined
  children: ReactNode
}

type PopoverEl = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }

const ITEM_SELECTOR = '[role^="menuitem"]:not(:disabled)'
const EDGE = 8
const GAP = 4

/**
 * Menu verticale M3 Expressive (raggio 16, surface-container-low, livello 2, voci da 44).
 * Tastiera: frecce su/giu' (con giro), Home/End, lettere (ricerca per iniziale), Invio/Spazio scelgono,
 * Esc chiude e restituisce il focus, Tab chiude. Vive nel top layer (popover manuale): resta sopra i dialog.
 */
export function Menu({ anchorRef, open, onClose, 'aria-label': ariaLabel, className, children }: MenuProps) {
  const ref = useRef<PopoverEl | null>(null)
  const typed = useRef<{ text: string; at: number }>({ text: '', at: 0 })
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number; origin: 'top' | 'bottom' } | null>(null)

  const close = useCallback(() => {
    onClose()
    suppressTooltipsBriefly()
    anchorRef.current?.focus()
  }, [onClose, anchorRef])

  // Posizione sotto (o sopra) l'elemento di ancoraggio.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const menu = ref.current
    const anchor = anchorRef.current
    if (!menu || !anchor) return
    try { menu.showPopover?.() } catch { /* senza supporto resta position: fixed */ }
    const a = anchor.getBoundingClientRect()
    const m = menu.getBoundingClientRect()
    // Il menu resta nel segmento dell'ancora (mai a cavallo della cerniera).
    const bd = boundsFor(a)
    const below = bd.bottom - a.bottom - GAP - EDGE
    const above = a.top - bd.top - GAP - EDGE
    const flip = m.height > below && above > below
    const space = flip ? above : below
    const top = flip ? Math.max(bd.top + EDGE, a.top - GAP - Math.min(m.height, space)) : a.bottom + GAP
    let left = a.left
    if (left + m.width > bd.right - EDGE) left = a.right - m.width
    left = Math.max(bd.left + EDGE, left)
    setPos({ left, top, maxHeight: Math.max(120, space), origin: flip ? 'bottom' : 'top' })
  }, [open, anchorRef])

  // Focus iniziale: voce selezionata, altrimenti la prima attiva.
  useEffect(() => {
    if (!open || !pos) return
    const menu = ref.current
    if (!menu || menu.contains(document.activeElement)) return
    const items = menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)
    const selected = menu.querySelector<HTMLElement>('[aria-checked="true"]:not(:disabled)')
    ;(selected ?? items[0])?.focus()
  }, [open, pos])

  // Click fuori e ridimensionamento chiudono senza rubare il focus.
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return
      onClose()
    }
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return
      onClose()
    }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('resize', onClose)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('resize', onClose)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(ITEM_SELECTOR))
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    const go = (i: number) => {
      e.preventDefault()
      items[(i + items.length) % items.length]?.focus()
    }
    if (e.key === 'ArrowDown') go(current + 1)
    else if (e.key === 'ArrowUp') go(current < 0 ? items.length - 1 : current - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(items.length - 1)
    else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      close()
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
      const now = Date.now()
      const t = typed.current
      t.text = now - t.at > 700 ? e.key.toLowerCase() : t.text + e.key.toLowerCase()
      t.at = now
      const start = t.text.length === 1 ? current + 1 : current
      for (let k = 0; k < items.length; k += 1) {
        const item = items[(start + k) % items.length]
        if (item?.textContent?.trim().toLowerCase().startsWith(t.text)) {
          go((start + k) % items.length)
          break
        }
      }
    }
  }

  return (
    <MenuContext.Provider value={{ close }}>
      <div
        ref={ref}
        role="menu"
        aria-label={ariaLabel}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({ popover: 'manual' } as any)}
        className={cx('md-menu', className)}
        data-origin={pos?.origin}
        style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight } : { left: 0, top: 0, visibility: 'hidden' }}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </MenuContext.Provider>
  )
}

export interface MenuItemProps {
  children: ReactNode
  icon?: string | undefined
  /** Se definito la voce e' una scelta (menuitemradio) e, se true, ha il contenitore tertiary. */
  selected?: boolean | undefined
  disabled?: boolean | undefined
  onSelect?: (() => void) | undefined
  className?: string | undefined
}

export function MenuItem({ children, icon, selected, disabled, onSelect, className }: MenuItemProps) {
  const ctx = useContext(MenuContext)
  return (
    <button
      type="button"
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={selected === undefined ? undefined : selected}
      disabled={disabled}
      tabIndex={-1}
      className={cx('md-menu__item', 'md-state', selected && 'md-menu__item--selected', className)}
      onClick={() => {
        onSelect?.()
        ctx?.close()
      }}
    >
      {icon ? <Icon name={icon} size={20} /> : null}
      <span className="md-menu__label">{children}</span>
    </button>
  )
}

export function MenuDivider() {
  return <hr role="separator" className="md-menu__divider" />
}

export interface OverflowMenuItem {
  label: string
  icon?: string | undefined
  onSelect: () => void
  disabled?: boolean | undefined
}

export interface OverflowMenuProps {
  'aria-label': string
  items: readonly OverflowMenuItem[]
  size?: 'md' | 'sm' | undefined
}

/** IconButton `more_vert` che apre un Menu con le azioni in eccesso. */
export function OverflowMenu({ 'aria-label': ariaLabel, items, size = 'md' }: OverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement | null>(null)
  const dismiss = useCallback(() => setOpen(false), [])
  return (
    <>
      <IconButton
        ref={anchor}
        icon="more_vert"
        size={size}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      <Menu anchorRef={anchor} open={open} onClose={dismiss} aria-label={ariaLabel}>
        {items.map((it) => (
          <MenuItem key={it.label} icon={it.icon} disabled={it.disabled} onSelect={it.onSelect}>
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
